// Renders a slide from the granular elements_json/contents_json model –
// unlike slideRenderer.js (which builds template-shaped flow/flex HTML from
// raw AI fields), this walks a flat, absolutely-positioned list of elements
// and is template-agnostic: it only ever looks at element.type and box.
//
// An element's box in elements_json is the canonical position/size: there is
// no layout pass that re-measures or re-centres it. Grouped elements carry a
// group id, stamped here as data-group-id, which the editor uses to move the
// whole group together.

const SlideModelRenderer = (() => {
    function escapeHtml(str) {
        if (typeof str !== 'string') return '';
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    const rtm = (str) => MathRender.renderRichText(str);

    function unsplashAttrs(credit) {
        if (!credit || !credit.name) return '';
        return ` data-unsplash-id="${escapeHtml(credit.id || '')}"`
            + ` data-unsplash-credit="${escapeHtml(credit.name)}"`
            + ` data-unsplash-profile="${escapeHtml(credit.profileUrl || '')}"`;
    }

    // CSS custom properties (transform/style_overrides) an element can carry
    // besides position, size, and stacking order.
    //
    // Box is the canonical size, text boxes let overflow spill visibly
    function boxStyle(el) {
        let style = `position:absolute; left:${el.box.x}px; top:${el.box.y}px; `
            + `width:${el.box.width}px; height:${el.box.height}px;`;
        if (el.type !== 'image' && el.type !== 'shape') style += ' overflow: visible;';
        style += ` z-index:${el.z_index};`;
        const t = el.transform;
        if (t && (t.rotation || t.flipH || t.flipV)) {
            const scaleX = t.flipH ? -1 : 1;
            const scaleY = t.flipV ? -1 : 1;
            style += ` transform: rotate(${t.rotation || 0}deg) scale(${scaleX}, ${scaleY});`;
        }
        // style_overrides keys are camelCase (fontSize, textTransform…), but
        // inline style attributes are parsed as CSS declarations where camelCase
        // doesn't match the hyphenated property name – so serialize kebab-case.
        for (const [prop, value] of Object.entries(el.style_overrides || {})) {
            if (MARKER_KEYS.includes(prop)) continue;
            const name = prop.replace(/[A-Z]/g, c => '-' + c.toLowerCase());
            style += ` ${name}: ${value};`;
        }
        const a = el.type !== 'image' && el.type !== 'shape' ? el.align : null;
        if (a && typeof a === 'object') {
            if (a.h && a.h !== 'left' && ['left', 'center', 'right', 'justify'].includes(a.h)) {
                style += ` text-align: ${a.h};`;
            }
            if (a.v && a.v !== 'top' && ['top', 'middle', 'bottom'].includes(a.v)) {
                const justify = a.v === 'top' ? 'flex-start'
                    : a.v === 'bottom' ? 'flex-end' : 'center';
                style += ' display: flex; flex-direction: column;';
                style += ` justify-content: ${justify};`;
            }
        }
        return style;
    }

    // Composite types hold more than one named field in their content record
    // (a step's label+text, a term-grid item's term+definition) – rendered as
    // two children inside the one positioned box.
    const COMPOSITE_MARKUP = {
        flow_step: (c) => `<div class="el__label">${rtm(c.label)}</div><div class="el__text">${rtm(c.text)}</div>`,
        timeline_event: (c) => `<span class="el__label">${rtm(c.label)}</span><span class="el__text">${rtm(c.text)}</span>`,
        term_copy: (c) => `<div class="el__term">${rtm(c.term)}</div><div class="el__definition">${rtm(c.definition)}</div>`,
        example_box: (c, el) => `<div class="el__example-label">${escapeHtml(calloutLabel(el))}</div><div class="el__example-text">${rtm(c.text)}</div>`,
    };

    // An example box's callout kind (utils/templates.js CALLOUTS) picks its
    // label and tint; slides.css keys the tint off data-callout.
    const CALLOUT_LABELS = { example: 'Example', 'fun-fact': 'Fun fact', remember: 'Remember', 'try-this': 'Try this' };
    function calloutKind(el) {
        const kind = el.style_overrides && el.style_overrides.calloutKind;
        return Object.prototype.hasOwnProperty.call(CALLOUT_LABELS, kind) ? kind : 'example';
    }
    function calloutLabel(el) {
        return CALLOUT_LABELS[calloutKind(el)];
    }

    const BULLET_STYLES = ['main', 'dot'];
    // style_overrides keys that pick a template's markup variant rather than
    // set a CSS property – rendered as data attributes, never as inline style.
    const MARKER_KEYS = ['bulletStyle', 'calloutKind'];

    // Present mode brings in anything numbered data-reveal one click at a time
    // (see presentRevealSteps in app.js). An element's `reveal` is only its
    // place in the queue; the step numbers are worked out here, because a list
    // revealed bullet by bullet takes one step per bullet and the count of
    // bullets changes whenever the teacher edits the list.
    function revealSteps(elements, contents) {
        const steps = new Map();
        let next = 1;
        elements
            .filter(el => el && Number(el.reveal) > 0)
            .sort((a, b) => Number(a.reveal) - Number(b.reveal))
            .forEach((el) => {
                steps.set(el.id, next);
                const items = el.type === 'bullet_list' && el.reveal_items
                    ? (contents[el.content_id] || {}).items : null;
                next += Array.isArray(items) && items.length ? items.length : 1;
            });
        return steps;
    }

    function renderElement(el, contentsJson, followers, reveals) {
        const content = contentsJson[el.content_id] || {};
        const style = boxStyle(el);
        const visible = el.visible === false ? ' hidden' : '';
        const step = reveals ? reveals.get(el.id) : 0;
        const byItem = !!step && el.type === 'bullet_list' && !!el.reveal_items;
        const revealAttr = !step ? '' : byItem ? ` data-reveal-items="${step}"` : ` data-reveal="${step}"`;
        const dataId = ` data-el-id="${escapeHtml(el.id)}"${revealAttr}`;
        // Grouped elements carry the group id so the client can move every
        // member together on a drag without any DOM wrapper.
        const groupAttr = el.group ? ` data-group-id="${escapeHtml(String(el.group))}"` : '';
        const customFlags = (followers.get(el.id) ? ` data-match-host-height="${followers.get(el.id)}"` : '');

        if (el.type === 'image') {
            if (!content.url) return `<div class="el el--image-placeholder" style="${style}"${dataId}${groupAttr}${customFlags}${visible}></div>`;
            // A custom upload's CDN storage path is re-stamped on the <img> so
            // the drag editor's image management (delete from CDN, tracking)
            // survives a reload that re-renders the slide from the model.
            const imagePath = content.imagePath
                ? ` data-image-path="${escapeHtml(content.imagePath)}"`
                : '';
            return `<img class="el el--image" style="${style}"${dataId}${groupAttr}${customFlags}${visible} src="${escapeHtml(content.url)}" `
                + `alt="${escapeHtml(content.keyword || '')}" loading="lazy" `
                + `data-object-fit="${escapeHtml((el.image_fit && el.image_fit.mode) || 'contain')}"`
                + `${imagePath}`
                + `${unsplashAttrs(content.credit)}>`;
        }

        if (el.type === 'bullet_list') {
            const items = Array.isArray(content.items) ? content.items : [];
            const lis = items.map((item, i) => `<li${byItem ? ` data-reveal="${step + i}"` : ''}>${rtm(item)}</li>`).join('');
            // The marker a list's template draws (slides.css keys the ::before
            // off data-bullet) – not a CSS property, so it is kept out of style.
            const marker = el.style_overrides && BULLET_STYLES.includes(el.style_overrides.bulletStyle)
                ? ` data-bullet="${el.style_overrides.bulletStyle}"` : '';
            return `<ul class="el el--bullet_list" style="${style}"${dataId}${groupAttr}${customFlags}${visible}${marker}>${lis}</ul>`;
        }

        // Valigned text renders as a flex column, so its inline flow must stay
        // in ONE full-width item; lists/composites already stack block-wise.
        const valigned = el.align && typeof el.align === 'object'
            && ['middle', 'bottom'].includes(el.align.v);

        // Pure geometry – fill/radius come from style_overrides, no inner mark-up.
        if (el.type === 'shape') {
            // A teacher-chosen slide background is a full-canvas shape. It is
            // marked so the editor lets clicks through it to the slide.
            const bg = el.role === 'background' ? ' data-slide-bg' : '';
            return `<div class="el el--shape" style="${style}"${dataId}${groupAttr}${customFlags}${visible}${bg}></div>`;
        }

        const composite = COMPOSITE_MARKUP[el.type];
        if (composite) {
            const callout = el.type === 'example_box' ? ` data-callout="${calloutKind(el)}"` : '';
            return `<div class="el el--${el.type}" style="${style}"${dataId}${groupAttr}${customFlags}${visible}${callout}>${composite(content, el)}</div>`;
        }

        // Every other type is a single text field.
        const inner = valigned
            ? `<div class="el__va-wrap" style="width:100%">${rtm(content.text)}</div>`
            : rtm(content.text);
        return `<div class="el el--${el.type}" style="${style}"${dataId}${groupAttr}${customFlags}${visible}>${inner}</div>`;
    }

    // Shapes that must stay glued to a growing text host. Text renders height:auto,
    // so a filled shape's page space only lines up with the card it decorates if
    // it is re-anchored against the host's *rendered* box. The rule reads the box
    // alone – the relationship is declared in the blueprint and resolved into
    // geometry at build time, so no extra flag is persisted in elements_json:
    //   'follow' – a thin vertical accent spanning the host (definition bar):
    //              width <= 16, tall (w*2 < h), sharing the host's top edge.
    //   'pin'    – a hairline under the host (heading underline): height <= 8,
    //              wide (w > h*4), grouped with the text box it underlines.
    // Badges are excluded by shape: a process-flow circle (40x40, borderRadius
    // 50%) is not thin, a timeline dot is not tall and not a hairline.
    function hostHeightFollowers(elements) {
    const mode = new Map();
    const byGroup = new Map();
    for (const el of elements) {
        if (!el || !el.group) continue;
        if (!byGroup.has(el.group)) byGroup.set(el.group, []);
        byGroup.get(el.group).push(el);
    }
    for (const members of byGroup.values()) {
        const shapes = members.filter(m => m.type === 'shape');
        const hosts = members.filter(m => m.type !== 'shape');
        if (!shapes.length || !hosts.length) continue;
        // The accent's host is the largest text element in its group – the card
        // the bar sits beside – never a sibling badge element (a process circle
        // shares a box with its number).
        const host = hosts.reduce((a, b) => (a.box.width * a.box.height >= b.box.width * b.box.height ? a : b));
        const hb = host.box;
        for (const s of shapes) {
            const { width, height } = s.box;
            if (width <= 16 && width * 2 < height && s.box.y === hb.y) mode.set(s.id, 'follow');
            else if (height <= 8 && width > height * 4) mode.set(s.id, 'pin');
        }
    }
    return mode;
}

function buildHtml(contentsJson, elementsJson) {
        const contents = contentsJson || {};
        const elements = Array.isArray(elementsJson) ? elementsJson : [];
        const followers = hostHeightFollowers(elements);
        const reveals = revealSteps(elements, contents);
        const body = elements
            .slice()
            .sort((a, b) => (a.z_index || 0) - (b.z_index || 0))
            .map(el => renderElement(el, contents, followers, reveals))
            .join('\n');
        return `<div class="slide slide--elements" data-no-autofit>${body}</div>`;
    }

    // object-fit can't be set from a CSS class alone per-element without one
    // rule per mode, so it rides in on data-object-fit and is applied here.
    function applyImageFit(container) {
        container.querySelectorAll('.el--image[data-object-fit]').forEach(img => {
            img.style.objectFit = img.dataset.objectFit;
        });
    }

    // Shapes stamped data-match-host-height stay glued to their grouped text
    // host's rendered box: text boxes grow with their content (height:auto)
    // while shapes keep fixed sizes, so a definition card accent bar would
    // otherwise shrink relative to a long card and a two-line heading would
    // leave its underline floating mid-text. Runs after math renders, because
    // KaTeX can change the host's height.
    function trackHostHeights(container) {
        container.querySelectorAll('[data-match-host-height]').forEach(tracker => {
            const gid = tracker.dataset.groupId;
            const mode = tracker.dataset.matchHostHeight;
            if (!gid || !mode) return;
            const esc = (typeof CSS !== 'undefined' && CSS.escape)
                ? CSS.escape(gid)
                : String(gid).replace(/["\\]/g, '\\$&');
            const host = Array.from(container.querySelectorAll(`[data-group-id="${esc}"]`))
                .filter(member => member !== tracker && member.offsetHeight > 0)
                .sort((a, b) => b.offsetWidth * b.offsetHeight - a.offsetWidth * a.offsetHeight)[0];
            if (!host) return;
            if (mode === 'pin') {
                // Hairline (heading underline): keep its height and sit a small
                // margin below the host's rendered bottom edge – the same 8px
                // gap baked into its blueprint box position.
                const PIN_MARGIN = 8;
                tracker.style.top = (host.offsetTop + host.offsetHeight + PIN_MARGIN) + 'px';
            } else {
                // Full-height accent (definition bar): stretch between the
                // host's rendered top and bottom.
                tracker.style.height = host.offsetHeight + 'px';
                tracker.style.top = host.offsetTop + 'px';
            }
        });
    }

    function renderSlideHtml(container, contentsJson, elementsJson) {
        container.innerHTML = buildHtml(contentsJson, elementsJson);
        applyImageFit(container);
        MathRender.renderMathInElement(container);
        trackHostHeights(container);
    }

    return { buildHtml, renderSlideHtml };
})();

if (typeof module === 'object' && module.exports) module.exports = SlideModelRenderer;
